import type { QuestProgressEntry } from 'eolib';
import { QuestPage, QuestRequirementIcon } from 'eolib';
import { useEffect, useState } from 'preact/hooks';
import {
  FaBoxOpen,
  FaCheckCircle,
  FaCommentDots,
  FaMapMarkerAlt,
  FaSkull,
} from 'react-icons/fa';
import { QuestBookList, Tabs } from '@/ui/components';
import { useClient, useLocale } from '@/ui/context';
import { DialogBase } from './dialog-base';
import {EoWriter,PacketAction} from 'eolib';
import {FAMILY_LIFE_SKILLS} from '@/life-skills';

type QuestTab = 'active' | 'history';

function questIcon(icon: QuestRequirementIcon) {
  switch (icon) {
    case QuestRequirementIcon.Item:
      return <FaBoxOpen size={14} />;
    case QuestRequirementIcon.Talk:
      return <FaCommentDots size={14} />;
    case QuestRequirementIcon.Kill:
      return <FaSkull size={14} />;
    case QuestRequirementIcon.Step:
      return <FaMapMarkerAlt size={14} />;
    default:
      return <FaCheckCircle size={14} />;
  }
}

function ActiveTab({
  entries,
  tracked,
  onTrack,
  onRetire,
}: {
  entries: QuestProgressEntry[];
  tracked: string[];
  onTrack: (name: string) => void;
  onRetire:(name:string)=>void;
}) {
  const { locale } = useLocale();

  if (entries.length === 0) {
    return (
      <p class='py-4 text-center text-sm opacity-60'>
        {locale.quests.noActive}
      </p>
    );
  }

  return (
    <ul class='flex flex-col gap-2'>
      {entries.map((entry, i) => {
        const isTracked = tracked.includes(entry.name);
        const hasProgress = entry.target > 0;

        return (
          <li
            key={i}
            class='flex flex-col gap-1 rounded border border-base-content/10 p-2'
          >
            <div class='flex items-start gap-2'>
              <span class='mt-0.5 shrink-0 text-primary'>
                {questIcon(entry.icon)}
              </span>
              <div class='min-w-0 flex-1'>
                <div class='flex items-center justify-between gap-2'>
                  <span class='truncate font-semibold text-sm'>
                    {entry.name}
                  </span>
                  <label class='flex cursor-pointer items-center gap-1 text-xs'>
                    <input
                      type='checkbox'
                      class='checkbox checkbox-xs'
                      checked={isTracked}
                      onChange={() => onTrack(entry.name)}
                    />
                    {locale.quests.track}
                  </label>
                </div>
                {entry.description && (
                  <p class='text-xs leading-snug opacity-70'>
                    {entry.description}
                  </p>
                )}
              </div>
            </div>
            {hasProgress && (
              <div class='flex items-center gap-2 pl-6'>
                <progress
                  class='progress progress-primary h-1.5 flex-1'
                  value={entry.progress}
                  max={entry.target}
                />
                <span class='shrink-0 text-xs opacity-60'>
                  {entry.progress}/{entry.target}
                </span>
              </div>
            )}
            <button type='button' class='btn btn-ghost btn-xs self-end' onClick={()=>onRetire(entry.name)}>Retire quest</button>
          </li>
        );
      })}
    </ul>
  );
}

export function QuestsDialog() {
  const client = useClient();
  const { locale } = useLocale();
  const [retiring,setRetiring]=useState<string|null>(null);
  const [retireMessage,setRetireMessage]=useState('');
  useEffect(()=>{const receive=(event:Event)=>{const data=(event as CustomEvent<{error?:string;message?:string}>).detail;setRetireMessage(data.error??data.message??'');setRetiring(null);client.questController.refreshQuestProgress();};window.addEventListener('lulseo-quest-retired',receive);return()=>window.removeEventListener('lulseo-quest-retired',receive);},[client]);
  const retire=()=>{if(!retiring)return;const writer=new EoWriter();writer.addString(JSON.stringify({action:'quest_retire',node:retiring}));client.bus.sendBuf(FAMILY_LIFE_SKILLS,PacketAction.Request,writer.toByteArray());setRetiring(null);};

  const [activeTab, setActiveTab] = useState<QuestTab>('active');
  const [progressEntries, setProgressEntries] = useState<QuestProgressEntry[]>(
    () => client.questController.progressQuests,
  );
  const [historyEntries, setHistoryEntries] = useState<string[]>(
    () => client.questController.historyQuests,
  );
  const [tracked, setTracked] = useState<string[]>(
    () => client.questController.trackedQuestNames,
  );

  useEffect(() => {
    const handleQuestList = (
      page: QuestPage,
      progress: QuestProgressEntry[],
      history: string[],
    ) => {
      if (page === QuestPage.Progress) {
        setProgressEntries([...progress]);
      } else {
        setHistoryEntries([...history]);
      }
    };
    const handleTrackedChanged = (names: string[]) => {
      setTracked([...names]);
    };

    client.questController.subscribeQuestListReceived(handleQuestList);
    client.questController.subscribeTrackedQuestChanged(handleTrackedChanged);

    // Fetch progress when dialog mounts
    client.questController.refreshQuestProgress();

    return () => {
      client.questController.unsubscribeQuestListReceived(handleQuestList);
      client.questController.unsubscribeTrackedQuestChanged(
        handleTrackedChanged,
      );
    };
  }, [client]);

  // Fetch history when switching to history tab
  const handleTabChange = (id: string) => {
    const tab = id as QuestTab;
    setActiveTab(tab);
    if (tab === 'history') {
      client.questController.refreshQuestHistory();
    }
  };

  const handleTrack = (name: string) => {
    client.questController.toggleTrackedQuest(name);
  };

  const TABS = [
    { id: 'active', label: locale.quests.tabActive },
    { id: 'history', label: locale.quests.tabHistory },
  ] as const;

  return (
    <DialogBase id='quests' title={locale.quests.title} size='md'>
      <div class='flex max-h-80 flex-col gap-2 overflow-y-auto p-1'>
        {retiring&&<div class='rounded border border-base-content/20 bg-base-200 p-3 text-sm'><p>Retire {retiring}? Your unfinished objectives will be reset. You can start again at the quest giver.</p><div class='mt-2 flex gap-2'><button type='button' class='btn btn-warning btn-sm' onClick={retire}>Retire</button><button type='button' class='btn btn-ghost btn-sm' onClick={()=>setRetiring(null)}>Keep quest</button></div></div>}
        {retireMessage&&<p class='text-sm' role='status'>{retireMessage}</p>}
        <Tabs
          items={TABS as unknown as { id: string; label: string }[]}
          activeId={activeTab}
          onSelect={handleTabChange}
          style='border'
          size='sm'
        />
        {activeTab === 'active' && (
          <ActiveTab
            entries={progressEntries}
            tracked={tracked}
            onTrack={handleTrack}
            onRetire={setRetiring}
          />
        )}
        {activeTab === 'history' && (
          <QuestBookList questNames={historyEntries} />
        )}
      </div>
    </DialogBase>
  );
}
